import { OrgChart } from 'd3-org-chart'
import { useEffect, useRef } from 'react';
import styled from 'styled-components';

const NODE_WIDTH = 300;
const NODE_HEIGHT = 132;

const ChartWrapper = styled.div`
    flex: 1 1 auto;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    position: relative;
    box-sizing: border-box;
    background:
        radial-gradient(circle at 1px 1px, rgba(15, 23, 42, 0.08) 1px, transparent 0) 0 0/20px 20px,
        linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%);
    border-radius: 12px;
    box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.05);

    & > div {
        width: 100%;
        height: 100%;
    }
    svg {
        display: block;
    }

    /* remove o retângulo padrão que envolve cada nó */
    .node-rect,
    rect.node-rect {
        fill: none !important;
        stroke: none !important;
    }

    /* card */
    .org-card {
        position: relative;
        width: ${NODE_WIDTH}px;
        height: ${NODE_HEIGHT}px;
        background: #ffffff;
        border-radius: 12px;
        box-shadow:
            0 4px 12px rgba(15, 23, 42, 0.08),
            0 1px 3px rgba(15, 23, 42, 0.06);
        overflow: hidden;
        font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
        border: 1px solid rgba(15, 23, 42, 0.06);
        box-sizing: border-box;
        transition: transform 0.18s ease, box-shadow 0.18s ease;
    }
    .org-card:hover {
        transform: translateY(-2px);
        box-shadow:
            0 12px 24px rgba(15, 23, 42, 0.14),
            0 4px 8px rgba(15, 23, 42, 0.08);
    }
    .org-card__bar {
        height: 6px;
        background: linear-gradient(90deg, #2563eb 0%, #4f46e5 60%, #7c3aed 100%);
    }
    .org-card__body {
        padding: 12px 14px 10px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        height: calc(100% - 6px);
        box-sizing: border-box;
    }
    .org-card__name,
    .org-card__title,
    .org-card__contrato,
    .org-card__cc {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
    }
    .org-card__name {
        font-size: 15px;
        font-weight: 700;
        color: #0f172a;
        line-height: 1.2;
    }
    .org-card__title {
        font-size: 13px;
        font-weight: 500;
        color: #475569;
    }
    .org-card__contrato {
        font-size: 11px;
        color: #64748b;
    }
    .org-card__cc {
        margin-top: auto;
        align-self: flex-start;
        font-size: 11px;
        font-weight: 600;
        color: #1e3a8a;
        background: #dbeafe;
        padding: 3px 8px;
        border-radius: 999px;
    }

    /* botão de expandir/colapsar */
    .org-toggle {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 999px;
        color: #1e293b;
        font-size: 12px;
        font-weight: 600;
        font-family: 'Inter', system-ui, sans-serif;
        box-shadow: 0 2px 4px rgba(15, 23, 42, 0.06);
        cursor: pointer;
        transition: background 0.15s ease, transform 0.15s ease;
    }
    .org-toggle:hover {
        background: #f1f5f9;
    }
    .org-toggle__icon {
        font-size: 14px;
        line-height: 1;
        color: #2563eb;
    }
`;

const escapeHtml = (str) => {
    if (str === null || str === undefined) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
};

const OrganogramaContainer = ({ dados }) => {

    const wrapperRef = useRef(null)
    const chartRef = useRef(null)
    const chartInstance = useRef(null)

    useEffect(() => {

        if (!dados || dados.length === 0) return;
        if (!wrapperRef.current || !chartRef.current) return;

        const { clientWidth, clientHeight } = wrapperRef.current;

        chartInstance.current = new OrgChart()
            .container(chartRef.current)
            .data(dados)
            .svgWidth(clientWidth)
            .svgHeight(clientHeight)
            .nodeHeight(() => NODE_HEIGHT)
            .nodeWidth(() => NODE_WIDTH)
            .childrenMargin(() => 80)
            .siblingsMargin(() => 25)
            .neighbourMargin(() => 30)
            .compactMarginBetween(() => 55)
            .compactMarginPair(() => 80)
            .linkYOffset(0)
            .duration(450)
            .linkUpdate(function () {
                this.setAttribute('stroke', '#94a3b8');
                this.setAttribute('stroke-width', '1.5');
                this.setAttribute('stroke-dasharray', '0');
            })
            .buttonContent(({ node }) => {
                const expanded = !node.children && node._children;
                const total = node.data._directSubordinates ?? 0;
                const icon = expanded ? '▸' : '▾';
                return `
                    <div class="org-toggle">
                        <span class="org-toggle__icon">${icon}</span>
                        <span>${total}</span>
                    </div>
                `;
            })
            .nodeContent((d) => {
                const name = escapeHtml(d.data.name);
                const title = escapeHtml(d.data.title);
                const cc = escapeHtml(d.data.centro_custo);
                const contrato = escapeHtml(d.data.contrato || '-');
                return `
                    <div class="org-card">
                      <div class="org-card__bar"></div>
                      <div class="org-card__body">
                        <div class="org-card__name" title="${name}">${name}</div>
                        <div class="org-card__title" title="${title}">${title}</div>
                        <div class="org-card__contrato" title="${contrato}">${contrato}</div>
                        <div class="org-card__cc" title="${cc}">${cc}</div>
                      </div>
                    </div>
                `;
            })
            .render()
            .fit();

        const handleResize = () => {
            if (!chartInstance.current || !wrapperRef.current) return;
            chartInstance.current
                .svgWidth(wrapperRef.current.clientWidth)
                .svgHeight(wrapperRef.current.clientHeight)
                .render();
        };

        const observer = new ResizeObserver(handleResize);
        observer.observe(wrapperRef.current);

        return () => observer.disconnect();

    }, [dados])

    return (
        <ChartWrapper ref={wrapperRef}>
            <div ref={chartRef} />
        </ChartWrapper>
    )

}

export default OrganogramaContainer;
